/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Grid, Stack, styled } from '@mui/material';
import DialogTextField from 'components/dialog/DialogTextField';
import DialogDatePicker from 'components/dialog/DialogDatePicker';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { useProject } from 'hooks/useProject';
import { DateValidationError } from '@mui/x-date-pickers';
import { useAppDispatch } from 'store';
import { openDialog } from 'store/dialog/dialogSlice';
import isEmpty from 'lodash/isEmpty';
import { IDCStepper1Props } from 'interface/project/projectInfo';
import FindUserDialog from 'components/dialog/FindUserDialog';
import { useLocation } from 'react-router-dom';
import FileUploadAction from '../FileUploadAction';
import FileUpload from '../FileUpload';

const GridItem = styled(Grid)(() => ({
  paddingTop: '0px!important',
}));

function StepperDC1(): JSX.Element {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { getCommCodes, getProjectIdDetail, getProjectUsersList } = useProject();
  const { dcInputs, onChangeDC } = useProjectInfoProvider();
  const [errorProjectStartAt, setErrorProjectStartAt] = useState<DateValidationError | null>(null);
  const [errorProjectEndAt, setErrorProjectEndAt] = useState<DateValidationError | null>(null);
  const { data: commCodesData } = getCommCodes();
  const { data, isLoading } = getProjectIdDetail(dcInputs.projectId || '');

  const { data: usersListData, isLoading: usersListLoading } = getProjectUsersList(dcInputs.projectId || '');
  const totalCount = useMemo(() => {
    return usersListData?.totalCount ?? 0;
  }, [usersListData?.totalCount]);

  useEffect(() => {
    if (!isLoading && data?.inspection && dcInputs.projectId) {
      onChangeDC('inspectionId', data?.inspection?.id);
      onChangeDC('stepper1.ipsativeExcel.file.name', data?.inspection?.inspectionFilename || '');
      onChangeDC('stepper1.ipsativeExcel.rowCount', data?.inspection?.inspectionIpsatives?.length || 0);
    }
  }, [data?.inspection, dcInputs.projectId, isLoading, onChangeDC]);

  const handleFindInfoByCodeValue = useCallback(
    (codeValue: string): string => {
      return commCodesData?.find(code => code.codeValue === codeValue)?.codeName || '-';
    },
    [commCodesData],
  );
  const { projectType: inputProjectType, stepper1, projectId } = dcInputs;
  const { projectName, compName, projectStartAt, projectEndAt, compAdminList } = stepper1;
  useEffect(() => {
    if (errorProjectEndAt) {
      onChangeDC('stepper1.projectEndAt', null);
    }
  }, [errorProjectEndAt, onChangeDC]);

  useEffect(() => {
    if (stepper1.userExcel?.rowCount === 0 && !usersListLoading) {
      onChangeDC('stepper1.userExcel.rowCount', totalCount);
    }
  }, [onChangeDC, projectId, stepper1.userExcel?.rowCount, totalCount, usersListLoading]);

  const handleChange = useCallback(
    (name: string, value: any) => {
      onChangeDC(`stepper1.${name}`, value);
    },
    [onChangeDC],
  );

  const handleDateError = (name: string, value: any) => {
    if (name === 'projectStartAt') {
      setErrorProjectStartAt(value);
    } else if (name === 'projectEndAt') {
      setErrorProjectEndAt(value);
    }
  };

  const errorMessage = (error: any) => {
    switch (error) {
      case 'maxDate':
      case 'minDate': {
        return '년도를 다시 확인해주세요.';
      }

      case 'invalidDate': {
        return '날짜가 유효하지 않습니다.';
      }

      default: {
        return '';
      }
    }
  };
  const handleFindUser = () => {
    dispatch(
      openDialog({
        content: <FindUserDialog stateKey="stepper1.compAdminList" onChange={onChangeDC} />,
      }),
    );
  };

  const handleAccountsListByComma = useMemo(() => {
    if (!compAdminList || compAdminList.length === 0) {
      return '';
    }
    return compAdminList?.map(obj => obj.name)?.join(', ');
  }, [compAdminList]);

  const handleStepper1ValuesValidCheck = useMemo((): boolean => {
    const requiredKeys: (keyof IDCStepper1Props)[] = [
      'projectName',
      'projectStartAt',
      'projectEndAt',
      'compName',
      'compAdminList',
    ];
    return (
      requiredKeys.every(key => {
        const tmpCheck = dcInputs.stepper1[key];
        return !isEmpty(tmpCheck);
      }) && !isEmpty(dcInputs.projectType)
    );
  }, [dcInputs]);

  useEffect(() => {
    onChangeDC(
      'stepper1.isStep1Valid',
      location.state?.rowInfo
        ? handleStepper1ValuesValidCheck
        : dcInputs.stepper1.ipsativeExcel.file != null &&
            dcInputs.stepper1.ipsativeExcel.rowCount > 0 &&
            dcInputs.stepper1.userExcel.file != null &&
            dcInputs.stepper1.userExcel.rowCount > 0,
    );
  }, [
    dcInputs.stepper1.ipsativeExcel.file,
    dcInputs.stepper1.ipsativeExcel.rowCount,
    dcInputs.stepper1.userExcel.file,
    dcInputs.stepper1.userExcel.rowCount,
    handleStepper1ValuesValidCheck,
    location.state?.rowInfo,
    onChangeDC,
  ]);

  const handleUserFileReset = () => {
    onChangeDC('stepper1.userExcel', {
      file: null,
      rowCount: 0,
      excelData: [],
    });
  };
  const handleUserFileChange = ({ file, rows, action }: { file: File; rows: any[][]; action: string }) => {
    onChangeDC('stepper1.userExcel', {
      file,
      rowCount: rows.length,
      excelData: rows,
      action,
    });
  };
  const handleIpsativeFileReset = () => {
    onChangeDC('stepper1.ipsativeExcel', {
      file: null,
      rowCount: 0,
      excelData: [],
      action: '',
    });
  };
  const handleIpsativeFileChange = ({ file, rows }: { file: File; rows: any[][] }) => {
    onChangeDC('stepper1.ipsativeExcel', {
      file,
      rowCount: rows.length,
      excelData: rows,
    });
  };
  return (
    <Grid item xs={12}>
      <Box sx={{ px: '1.625rem' }}>
        <Grid
          container
          spacing={2}
          sx={{
            mt: '1.5rem',
            width: '100%',
            display: 'flex',
            justifyContent: { xs: 'flex-start', lg: 'center' },
          }}
        >
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 유형"
              text={handleFindInfoByCodeValue(inputProjectType)}
              variant="outlined"
              name="projectType"
              disabled
            />
          </GridItem>
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 이름"
              text={projectName}
              variant="outlined"
              name="projectName"
              onChange={e => handleChange('projectName', e.target.value)}
            />
          </GridItem>
          <GridItem item xs={10}>
            <DialogTextField
              label="프로젝트 기업명"
              text={compName}
              variant="outlined"
              name="compName"
              onChange={e => handleChange('compName', e.target.value)}
            />
          </GridItem>
          <GridItem item xs={10}>
            <Stack
              sx={{ marginBottom: '20px', marginTop: '1px' }}
              spacing={2}
              direction={{ xs: 'column', sm: 'column', md: 'row' }}
              alignItems="flex-start"
            >
              <Typography
                sx={{
                  color: 'mind.black.A0',
                  minWidth: '100px',
                  typography: 's13w800',
                  maxWidth: { xs: '100%', sm: '100%', md: '100px' },
                  width: { xs: '100%', sm: '100%', md: '100px' },
                  mt: '12px!important',
                }}
                noWrap
              >
                프로젝트 기간
              </Typography>
              <DialogDatePicker
                label="시작일"
                labelWidth={40}
                name="projectStartAt"
                value={projectStartAt}
                errorMessage={errorMessage(errorProjectStartAt)}
                handleDateChange={handleChange}
                handleDateError={handleDateError}
              />
              <DialogDatePicker
                label="종료일"
                labelWidth={40}
                name="projectEndAt"
                value={projectEndAt}
                errorMessage={errorMessage(errorProjectEndAt)}
                handleDateChange={handleChange}
                handleDateError={handleDateError}
                minDate={projectStartAt as any}
              />
            </Stack>
          </GridItem>
          <GridItem item xs={10} sm={10} md={8}>
            <DialogTextField
              label="프로젝트 담당자"
              text={handleAccountsListByComma}
              variant="outlined"
              name="compAdminList"
            />
          </GridItem>
          <GridItem
            item
            xs={10}
            sm={10}
            md={2}
            alignItems="flex-start"
            justifyContent="center"
            sx={{ display: 'flex', marginBottom: '20px' }}
          >
            <Button
              onClick={handleFindUser}
              color="primary"
              variant="contained"
              sx={{ width: '100%', height: 41, textWrap: 'nowrap' }}
            >
              담당자 찾기
            </Button>
          </GridItem>
          <GridItem item xs={10} sm={10} md={8}>
            <FileUploadAction
              type="template1"
              label="명단 업로드"
              onReset={handleUserFileReset}
              onChange={handleUserFileChange}
              marginBottom="16px"
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mr: 2 }}>
                {dcInputs.stepper1.userExcel?.file ? (
                  <Typography>{dcInputs.stepper1.userExcel?.file.name}</Typography>
                ) : (
                  dcInputs.stepper1.projectPartUserFileName || '파일을 선택해주세요'
                )}
              </Box>
            </FileUploadAction>
          </GridItem>
          <GridItem
            item
            xs={10}
            sm={10}
            md={2}
            alignItems="flex-start"
            justifyContent="center"
            sx={{ display: 'flex' }}
          >
            <Button
              color="primary"
              variant="outlined"
              sx={{
                width: '100%',
                height: 41,
                textWrap: 'nowrap',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(49, 84, 234, 0.5)',
                  cursor: 'default',
                },
              }}
            >
              {dcInputs.stepper1.userExcel?.rowCount || 0}명
            </Button>
          </GridItem>
          <GridItem item xs={10} sm={10} md={8}>
            <FileUpload
              type="template2"
              label="문항 업로드"
              onReset={handleIpsativeFileReset}
              onChange={handleIpsativeFileChange}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mr: 2 }}>
                {dcInputs.stepper1.ipsativeExcel?.file ? (
                  <Typography>{dcInputs.stepper1.ipsativeExcel?.file.name}</Typography>
                ) : (
                  '파일을 선택해주세요'
                )}
              </Box>
            </FileUpload>
          </GridItem>
          <GridItem
            item
            xs={10}
            sm={10}
            md={2}
            alignItems="flex-start"
            justifyContent="center"
            sx={{ display: 'flex' }}
          >
            <Button
              color="primary"
              variant="outlined"
              sx={{
                width: '100%',
                height: 41,
                textWrap: 'nowrap',
                '&:hover': {
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(49, 84, 234, 0.5)',
                  cursor: 'default',
                },
              }}
            >
              {dcInputs.stepper1.ipsativeExcel?.rowCount || 0} 문항
            </Button>
          </GridItem>
        </Grid>
      </Box>
    </Grid>
  );
}

export default memo(StepperDC1);
